import React, { useEffect, useState } from 'react';
import { AppLayout } from '../components/layout/AppLayout';
import { useAuth } from '../auth/AuthContext';
import { useBilling, type SubscriptionStatus } from '../context/BillingContext';
import { api } from '../lib/apiClient';
import { Copy, Check, Trash2 } from 'lucide-react';
import type { ProjectSummary } from '../types/dashboard';

interface OrgInvite {
  id: string;
  code: string;
  maxUses: number | null;
  useCount: number;
  expiresAt: string | null;
  status: string;
  createdAt: string;
}

interface OrgMember {
  id: string;
  userId: string;
  email: string | null;
  role: string;
  orgWideRead: boolean;
  createdAt: string;
}

function SubscriptionStatusBadge({ status }: { status: SubscriptionStatus | null }) {
  const config: Record<string, { label: string; className: string }> = {
    active:     { label: 'Active',     className: 'bg-amber-500/20 text-amber-300' },
    past_due:   { label: 'Past due',   className: 'bg-orange-500/20 text-orange-300' },
    canceled:   { label: 'Canceled',   className: 'bg-red-500/20 text-red-300' },
    incomplete: { label: 'Incomplete', className: 'bg-slate-600 text-slate-300' },
  };
  const cfg = status ? config[status] : null;
  if (!cfg) {
    return <span className="text-slate-500 text-xs">No subscription</span>;
  }
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

export const SettingsPage: React.FC = () => {
  const { user, orgRole } = useAuth();
  const { subscriptionStatus, pilotExpiresAt, hasSubscription } = useBilling();
  const isAdmin = orgRole === 'admin';

  const [members, setMembers] = useState<OrgMember[]>([]);
  const [invites, setInvites] = useState<OrgInvite[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [managingSubscription, setManagingSubscription] = useState(false);

  // Shared project state for AppLayout (minimal — settings doesn't need projects)
  const [projects] = useState<ProjectSummary[]>([]);

  useEffect(() => {
    if (!isAdmin) return;
    loadMembers();
    loadInvites();
  }, [isAdmin]);

  const loadMembers = async () => {
    setLoadingMembers(true);
    try {
      const data = await api.get<{ ok: boolean; members: OrgMember[] }>('/org/members');
      setMembers(data.members ?? []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoadingMembers(false);
    }
  };

  const loadInvites = async () => {
    setLoadingInvites(true);
    try {
      const data = await api.get<{ ok: boolean; invites: OrgInvite[] }>('/org/invites');
      setInvites(data.invites ?? []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoadingInvites(false);
    }
  };

  const handleCreateInvite = async () => {
    setCreatingInvite(true);
    setError(null);
    try {
      const data = await api.post<{ ok: boolean; invite: OrgInvite }>('/org/invites', {});
      setInvites((prev) => [data.invite, ...prev]);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    try {
      await api.delete(`/org/invites/${inviteId}`);
      setInvites((prev) => prev.map((inv) =>
        inv.id === inviteId ? { ...inv, status: 'revoked' } : inv
      ));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleCopyInviteLink = (code: string) => {
    const link = `${window.location.origin}/login?invite=${code}`;
    navigator.clipboard.writeText(link);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleManageSubscription = async () => {
    setManagingSubscription(true);
    setError(null);
    try {
      const data = await api.post<{ ok: boolean; url: string }>('/billing/portal');
      window.open(data.url, '_blank');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setManagingSubscription(false);
    }
  };

  return (
    <AppLayout
      projects={projects}
      currentProjectId={null}
      onProjectChange={() => {}}
    >
      <div className="p-6 max-w-4xl mx-auto space-y-8">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">Settings</h1>
          <p className="mt-1 text-sm text-slate-400">
            Manage your account and organization.
          </p>
        </div>

        {/* Account info */}
        <section className="rounded-lg border border-slate-800 bg-slate-900/60 p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3">
            Account
          </h2>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-slate-400 w-20">Email:</span>
              <span className="text-slate-100">{user?.email ?? '—'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-400 w-20">Role:</span>
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                isAdmin
                  ? 'bg-amber-500/20 text-amber-300'
                  : 'bg-slate-700 text-slate-300'
              }`}>
                {orgRole ?? 'member'}
              </span>
            </div>
          </div>
        </section>

        {/* Billing */}
        <section className="rounded-lg border border-slate-800 bg-slate-900/60 p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3">
            Billing
          </h2>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-400 w-32">Status:</span>
              <SubscriptionStatusBadge status={subscriptionStatus} />
            </div>
            {pilotExpiresAt && subscriptionStatus === 'active' && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-400 w-32">Pilot expires:</span>
                <span className="text-slate-100">
                  {new Date(pilotExpiresAt).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
            )}
            {isAdmin && (
              <div className="pt-1 flex items-center gap-3">
                {hasSubscription ? (
                  <button
                    type="button"
                    onClick={handleManageSubscription}
                    disabled={managingSubscription}
                    className="inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-semibold bg-slate-700 text-slate-100 hover:bg-slate-600 disabled:opacity-50"
                  >
                    {managingSubscription ? 'Opening portal...' : 'Manage subscription'}
                  </button>
                ) : (
                  <a
                    href="/billing"
                    className="inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-semibold bg-amber-500 text-slate-900 hover:bg-amber-400"
                  >
                    Set up billing
                  </a>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Admin-only sections */}
        {isAdmin && (
          <>
            {/* Team members */}
            <section className="rounded-lg border border-slate-800 bg-slate-900/60 p-5">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3">
                Team members
              </h2>
              {loadingMembers ? (
                <p className="text-sm text-slate-500">Loading...</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-800 text-left text-xs text-slate-500 uppercase">
                        <th className="pb-2 pr-4">Email</th>
                        <th className="pb-2 pr-4">Role</th>
                        <th className="pb-2">Joined</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/50">
                      {members.map((member) => (
                        <tr key={member.id}>
                          <td className="py-2 pr-4 text-slate-200">{member.email ?? member.userId}</td>
                          <td className="py-2 pr-4">
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                              member.role === 'admin'
                                ? 'bg-amber-500/20 text-amber-300'
                                : 'bg-slate-700 text-slate-300'
                            }`}>
                              {member.role}
                            </span>
                          </td>
                          <td className="py-2 text-slate-400">
                            {new Date(member.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Invite codes */}
            <section className="rounded-lg border border-slate-800 bg-slate-900/60 p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
                  Invite codes
                </h2>
                <button
                  type="button"
                  onClick={handleCreateInvite}
                  disabled={creatingInvite}
                  className="inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-semibold bg-amber-500 text-slate-900 hover:bg-amber-400 disabled:opacity-50"
                >
                  {creatingInvite ? 'Creating...' : 'Generate invite code'}
                </button>
              </div>

              <p className="text-xs text-slate-500 mb-4">
                Share invite codes with teammates so they can join your organization.
                Users with personal emails (Gmail, Outlook, etc.) need an invite code to join.
              </p>

              {loadingInvites ? (
                <p className="text-sm text-slate-500">Loading...</p>
              ) : invites.length === 0 ? (
                <p className="text-sm text-slate-500">No invite codes yet. Generate one to invite teammates.</p>
              ) : (
                <div className="space-y-2">
                  {invites.map((invite) => {
                    const isActive = invite.status === 'active';
                    const isExpired = invite.expiresAt && new Date(invite.expiresAt) < new Date();
                    const isMaxed = invite.maxUses !== null && invite.useCount >= invite.maxUses;

                    return (
                      <div
                        key={invite.id}
                        className={`flex items-center justify-between rounded-lg border px-4 py-3 ${
                          isActive && !isExpired && !isMaxed
                            ? 'border-slate-700 bg-slate-800/50'
                            : 'border-slate-800/50 bg-slate-900/30 opacity-60'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <code className="font-mono text-sm font-semibold tracking-wider text-slate-100">
                            {invite.code}
                          </code>
                          <span className="text-xs text-slate-500">
                            {invite.useCount} use{invite.useCount !== 1 ? 's' : ''}
                            {invite.maxUses !== null ? ` / ${invite.maxUses} max` : ''}
                          </span>
                          {!isActive && (
                            <span className="text-xs text-red-400">revoked</span>
                          )}
                          {isExpired && isActive && (
                            <span className="text-xs text-amber-400">expired</span>
                          )}
                          {isMaxed && isActive && !isExpired && (
                            <span className="text-xs text-amber-400">limit reached</span>
                          )}
                        </div>

                        {isActive && !isExpired && !isMaxed && (
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleCopyCode(invite.code)}
                              className="rounded p-1.5 text-slate-400 hover:bg-slate-700 hover:text-slate-100"
                              title="Copy code"
                            >
                              {copiedCode === invite.code ? <Check size={14} /> : <Copy size={14} />}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCopyInviteLink(invite.code)}
                              className="rounded px-2 py-1 text-xs text-slate-400 hover:bg-slate-700 hover:text-slate-100"
                              title="Copy invite link"
                            >
                              Copy link
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRevokeInvite(invite.id)}
                              className="rounded p-1.5 text-slate-500 hover:bg-red-900/30 hover:text-red-400"
                              title="Revoke invite"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}

        {!isAdmin && (
          <section className="rounded-lg border border-slate-800 bg-slate-900/60 p-5">
            <p className="text-sm text-slate-400">
              Contact your organization admin to manage team settings and invite codes.
            </p>
          </section>
        )}

        {error && (
          <div className="rounded-lg border border-red-500/40 bg-red-950/30 px-4 py-3 text-sm text-red-100">
            {error}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default SettingsPage;
